import i18n from "../../i18n";
import translationEN from "../../locales/en/translation.json";
import translationFR from "../../locales/fr/translation.json";

const SUPPORTED_LOCALES = ["en", "fr"];
const STORAGE_KEY = "term-overrides-cache";

const DEFAULTS_BY_LOCALE = {
  en: { ...(translationEN.term || {}) },
  fr: { ...(translationFR.term || {}) },
};

const TERM_KEY_TO_I18N = {
  BLOCK: "block",
  CUSTOM_DIMENSIONS: "customDimensions",
  INPUT: "input",
  LABOR_RESOURCES: "laborResources",
  LIBRARY: "library",
  NON_LABOR_RESOURCES: "nonLaborResources",
  PROGRAM: "program",
  PROJECT: "project",
  SCOPE: "scope",
  SPACE: "space",
  STOCK: "stock",
  STRUCTURE: "structure",
};

export { TERM_KEY_TO_I18N };

// Term keys that have an irregular plural baked into the locale file
// (i.e. they're stored in singular form but used in both forms across the UI).
// When the customer overrides the singular, derive the plural by appending "s".
const SINGULAR_TO_PLURAL_KEY = {
  library: "libraries",
};

/**
 * Reads cached overrides from localStorage. Used by i18n.js to seed the
 * resource bundle BEFORE `i18n.init()` so module-level `i18n.t('term.*')`
 * calls (e.g. in Bryntum/Syncfusion configs) pick up customer wording on
 * first evaluation rather than the JSON defaults.
 */
export function readCachedTermOverrides() {
  try {
    const raw = typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
}

export function clearCachedTermOverrides() {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(STORAGE_KEY);
  } catch (_) { /* ignore */ }
}

/**
 * Replaces the `term.*` namespace in i18next with defaults plus customer overrides.
 * Always restores the full default set first so removing a customization on the
 * backend correctly reverts the UI on the next fetch.
 *
 * @param {{enabled: boolean, terms: Array<{term_key: string, custom_value: string}>}} settings
 */
export function applyTermOverrides(settings) {
  const overrideMap = {};
  if (settings && settings.enabled && Array.isArray(settings.terms)) {
    for (const t of settings.terms) {
      // The API has historically returned enum keys in upper case, but older
      // tenants can contain lower-case/whitespace variants. Normalize before
      // lookup so a language switch cannot silently drop a saved custom term.
      // Accept both the legacy snake_case DTO and the camelCase shape emitted
      // by newer API clients. The translation override must not disappear
      // after switching to French merely because the response serializer
      // changed its casing.
      const termKey = String(t?.term_key ?? t?.termKey ?? "")
        .trim()
        .toUpperCase();
      const i18nKey = TERM_KEY_TO_I18N[termKey];
      const rawCustomValue = t?.custom_value ?? t?.customValue;
      const customValue = typeof rawCustomValue === "string"
        ? rawCustomValue.trim()
        : "";
      if (i18nKey && customValue) {
        overrideMap[i18nKey] = customValue;
        // Auto-derive the irregular plural form so customer wording reaches
        // both singular and plural surfaces. Default English plural is kept
        // when no override is set (e.g. "Library" / "Libraries").
        const pluralKey = SINGULAR_TO_PLURAL_KEY[i18nKey];
        if (pluralKey) {
          overrideMap[pluralKey] = `${customValue}s`;
        }
      }
    }
  }
  for (const lng of SUPPORTED_LOCALES) {
    const merged = { ...DEFAULTS_BY_LOCALE[lng], ...overrideMap };
    i18n.addResourceBundle(lng, "translation", { term: merged }, true, true);
  }
  // Persist so the next bundle parse seeds module-level i18n.t() calls
  // (Bryntum/Syncfusion configs) with customer wording.
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(overrideMap));
    }
  } catch (_) { /* ignore quota errors */ }
}
